import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product, ProductImage } from '@skytech/shared';

import { item, track } from '../analytics/analytics';
import { api } from '../../lib/api';
import { toast } from '../../components/ui/toast';

/** A cart line as the UI renders it, in either guest or server mode. */
export interface CartLine {
  productId: string;
  quantity: number;
  name: string;
  slug: string;
  /** Unit price as a string (KES, no float). */
  price: string;
  image: ProductImage | null;
  /** Live stock ceiling; the stepper caps at this. */
  maxAvailable: number;
}

/** Minimal product shape needed to add a line (from a card or the PDP). */
export type AddableProduct = Pick<
  Product,
  'id' | 'name' | 'slug' | 'price' | 'stockQuantity' | 'images'
>;

interface ServerCartLine {
  productId: string;
  quantity: number;
  maxAvailable: number;
  lineTotal: string;
  product: { name: string; slug: string; price: string; image: ProductImage | null };
}
interface ServerCart {
  items: ServerCartLine[];
  subtotal: string;
  itemCount: number;
}

type Mode = 'guest' | 'server';

interface CartState {
  items: CartLine[];
  mode: Mode;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** `silent` suppresses auto-opening the drawer (the PDP shows a toast instead). */
  add: (product: AddableProduct, quantity?: number, opts?: { silent?: boolean }) => Promise<void>;
  setQuantity: (productId: string, quantity: number) => Promise<void>;
  remove: (productId: string) => Promise<void>;
  clear: () => Promise<void>;
  /** Order creation already clears the server cart transactionally (§4.6). */
  resetAfterOrder: () => void;
  /** Called by the auth store when a session resolves. */
  syncWithAuth: (authenticated: boolean) => Promise<void>;
}

function fromServer(cart: ServerCart): CartLine[] {
  return cart.items.map((i) => ({
    productId: i.productId,
    quantity: i.quantity,
    name: i.product.name,
    slug: i.product.slug,
    price: i.product.price,
    image: i.product.image,
    maxAvailable: i.maxAvailable,
  }));
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      mode: 'guest',
      isOpen: false,

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),

      async add(product, quantity = 1, opts) {
        const openAfter = opts?.silent ? get().isOpen : true;
        if (get().mode === 'server') {
          const res = await api.post<{ cart: ServerCart }>('/cart/items', {
            productId: product.id,
            quantity,
          });
          const before = get().items.find((l) => l.productId === product.id)?.quantity ?? 0;
          set({ items: fromServer(res.data.cart), isOpen: openAfter });
          const delta =
            (get().items.find((l) => l.productId === product.id)?.quantity ?? 0) - before;
          if (delta > 0)
            track('add_to_cart', {
              currency: 'KES',
              value: Number(product.price) * delta,
              items: [item(product, delta)],
            });
          return;
        }
        // Guest: sum with any existing line, cap at stock.
        const items = [...get().items];
        const idx = items.findIndex((l) => l.productId === product.id);
        const existing = idx >= 0 ? items[idx]!.quantity : 0;
        const capped = Math.min(existing + quantity, product.stockQuantity);
        const line: CartLine = {
          productId: product.id,
          quantity: capped,
          name: product.name,
          slug: product.slug,
          price: product.price,
          image: product.images[0] ?? null,
          maxAvailable: product.stockQuantity,
        };
        if (idx >= 0) items[idx] = line;
        else items.push(line);
        set({ items, isOpen: openAfter });
        if (capped > existing)
          track('add_to_cart', {
            currency: 'KES',
            value: Number(product.price) * (capped - existing),
            items: [item(product, capped - existing)],
          });
      },

      async setQuantity(productId, quantity) {
        const before = get().items.find((l) => l.productId === productId);
        if (quantity <= 0) return get().remove(productId);
        if (get().mode === 'server') {
          const res = await api.put<{ cart: ServerCart }>(`/cart/items/${productId}`, { quantity });
          set({ items: fromServer(res.data.cart) });
          measureCartChange(
            before,
            get().items.find((l) => l.productId === productId),
          );
          return;
        }
        set({
          items: get().items.map((l) =>
            l.productId === productId ? { ...l, quantity: Math.min(quantity, l.maxAvailable) } : l,
          ),
        });
        measureCartChange(
          before,
          get().items.find((l) => l.productId === productId),
        );
      },

      async remove(productId) {
        const before = get().items.find((l) => l.productId === productId);
        if (get().mode === 'server') {
          const res = await api.delete<{ cart: ServerCart }>(`/cart/items/${productId}`);
          set({ items: fromServer(res.data.cart) });
          measureCartChange(
            before,
            get().items.find((l) => l.productId === productId),
          );
          return;
        }
        set({ items: get().items.filter((l) => l.productId !== productId) });
        measureCartChange(before, undefined);
      },

      resetAfterOrder: () => set({ items: [] }),

      async clear() {
        if (get().mode === 'server') {
          await api.delete('/cart');
        }
        set({ items: [] });
      },

      async syncWithAuth(authenticated) {
        if (!authenticated) {
          set({ mode: 'guest' });
          return;
        }
        // Merge the guest cart into the server cart (sum + cap), then adopt the
        // server cart as the source of truth and drop the local guest copy.
        const guest =
          get().mode === 'guest'
            ? get().items.map((l) => ({ productId: l.productId, quantity: l.quantity }))
            : [];
        try {
          if (guest.length > 0) {
            const merged = await api.post<ServerCart>('/cart/merge', { items: guest });
            set({ items: fromServer(merged.data), mode: 'server' });
            // Let the user know their guest items weren't lost (App. H "Add to Cart Flow").
            toast.info('We combined your cart with your account.', {
              label: 'View cart',
              to: '/cart',
            });
          } else {
            const server = await api.get<ServerCart>('/cart');
            set({ items: fromServer(server.data), mode: 'server' });
          }
        } catch {
          // If the server is unreachable, stay in guest mode rather than losing items.
          set({ mode: 'guest' });
        }
      },
    }),
    {
      name: 'skytech-cart',
      skipHydration: true,
      // Only the guest cart is persisted; the server cart lives in the DB.
      partialize: (state) =>
        state.mode === 'guest'
          ? { items: state.items, mode: 'guest' as Mode }
          : { items: [], mode: 'guest' as Mode },
    },
  ),
);

/** Derived selectors. */
export const cartCount = (s: CartState): number => s.items.reduce((sum, l) => sum + l.quantity, 0);

export const cartSubtotal = (s: CartState): number =>
  s.items.reduce((sum, l) => sum + Number(l.price) * l.quantity, 0);

// ── Cart-abandonment tracking (SRS §5.6) ───────────────────────────────────────
// A stable anonymous id for this browser; also lets the server link a member's
// tracked cart to their account (the API client attaches the token when signed in).
const CART_SESSION_KEY = 'skytech-cart-session';

export function getCartSessionId(): string {
  let id = localStorage.getItem(CART_SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CART_SESSION_KEY, id);
  }
  return id;
}

/** After a successful checkout, start a fresh tracking session so the next cart is new. */
export function rotateCartSession(): void {
  localStorage.removeItem(CART_SESSION_KEY);
}

// Debounced, best-effort snapshot of the cart whenever its lines change. Purely
// telemetry for the abandoned-cart report — failures are swallowed and never block.
let trackTimer: ReturnType<typeof setTimeout> | undefined;
useCart.subscribe((state, prev) => {
  if (state.items === prev.items) return;
  clearTimeout(trackTimer);
  trackTimer = setTimeout(() => {
    void api
      .post('/cart/track', {
        sessionId: getCartSessionId(),
        items: state.items.map((l) => ({ productId: l.productId, quantity: l.quantity })),
      })
      .catch(() => {
        /* best-effort */
      });
  }, 1500);
});

function measureCartChange(before: CartLine | undefined, after: CartLine | undefined): void {
  if (!before) return;
  const delta = (after?.quantity ?? 0) - before.quantity;
  if (!delta) return;
  track(delta > 0 ? 'add_to_cart' : 'remove_from_cart', {
    currency: 'KES',
    value: Number(before.price) * Math.abs(delta),
    items: [
      {
        item_id: before.productId,
        item_name: before.name,
        price: Number(before.price),
        quantity: Math.abs(delta),
      },
    ],
  });
}
